import React from 'react';
import {useTranslation} from '@wix/yoshi-flow-editor';
import {useControllerProps} from '../../../../../domain/controllers/ControllerContext';
import s from './OrderSummary.scss';
import {SubTotals} from './SubTotals/SubTotals';
import {CouponTotal} from '../../../../../common/components/CouponTotal/CouponTotal';
import {Total} from './Total/Total';
import {PaymentBreakdown} from './PaymentBreakdown/PaymentBreakdown';
import {Spinner, SpinnerTypes} from 'wix-ui-tpa';
import {Violations} from '../../../../../common/components/Violations/Violations';
import {MinimumOrderAmount} from '../MinimumOrderAmount/MinimumOrderAmount';
import {CheckoutButtons} from '../CheckoutButtons/CheckoutButtons';
import {SecureCheckout} from '../../../../../common/components/SecureCheckout/SecureCheckout';
import {CartBodyDataHook} from '../../CartApp/partials/CartBody';
import {useIsAfterHydration} from '../../../../../hooks/useIsAfterHydration';
import {EstimateTotalsErrorNotification} from '../../../../../common/components/EstimateTotalsErrorNotification/EstimateTotalsErrorNotification';
import {CartSummarySkeleton} from './CartSummarySkeleton/CartSummarySkeleton';
import {CartDisclaimers} from './CartDisclaimers';

export enum OrderSummaryDataHook {
  Loader = 'OrderSummary.Loader',
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export const OrderSummary = () => {
  const {t} = useTranslation();
  const {cartStore} = useControllerProps();
  const {
    isSummaryUpdating,
    shouldShowSecureCheckout,
    shouldDisplayViolations,
    shouldShowMinimumOrderAmount,
    shouldEstimateTotals,
    isSummaryLoading,
    mapCartSummaryColors,
    errorOnEstimateTotals,
  } = cartStore;

  const isAfterHydration = useIsAfterHydration();

  const showAsideLoader = isSummaryLoading || (!isAfterHydration && shouldEstimateTotals);

  if (errorOnEstimateTotals) {
    return <EstimateTotalsErrorNotification />;
  }
  if (showAsideLoader) {
    return <CartSummarySkeleton />;
  }

  return (
    <div
      data-hook={CartBodyDataHook.CartAsideContent}
      className={mapCartSummaryColors ? s.cartSummary : /* istanbul ignore next */ undefined}>
      {isSummaryUpdating && <Spinner data-hook={OrderSummaryDataHook.Loader} isCentered type={SpinnerTypes.slim} />}
      <Totals />
      {shouldDisplayViolations && <Violations />}
      {shouldShowMinimumOrderAmount && <MinimumOrderAmount />}
      <CheckoutButtons />
      {shouldShowSecureCheckout && <SecureCheckout text={t('cart.orderSummary.secureCheckout.label')} />}
    </div>
  );
};

const Totals = () => {
  const {
    cartStore: {cartSummary, cartModel, refactorCouponOnCart, isSummaryUpdating},
  } = useControllerProps();

  const appliedCoupon = refactorCouponOnCart
    ? cartSummary?.couponDiscount && cartModel?.coupon
    : cartSummary?.appliedCoupon || cartModel?.coupon;

  return (
    <div className={isSummaryUpdating ? s.loading : undefined}>
      <SubTotals />
      {appliedCoupon && <CouponTotal />}
      <Total />
      <CartDisclaimers />
      <PaymentBreakdown />
    </div>
  );
};
